/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteShipments, changeSiteShipmentStatus } from "@/lib/api";

const GROUPS = [
  ["shipped", "Shipped"], ["in_transit", "In Transit"], ["delivered", "Delivered"], ["delivery_failed", "Delivery Problems"],
];
const ACTIONS_BY_STATUS = {
  shipped: [["mark_in_transit", "Mark In Transit"], ["mark_delivered", "Mark Delivered"], ["mark_delivery_failed", "Delivery Failed"]],
  in_transit: [["mark_delivered", "Mark Delivered"], ["mark_delivery_failed", "Delivery Failed"]],
  delivery_failed: [["mark_in_transit", "Retry — In Transit"], ["mark_returned", "Mark Returned"]],
};

export default function SiteShipping() {
  const dashboard = useSiteDashboard();
  const { token, slug } = dashboard;
  const [shipments, setShipments] = useState(null);

  const load = () => { if (token) fetchSiteShipments(token, slug).then(setShipments).catch(() => {}); };
  useEffect(load, [token, slug]);

  return (
    <SiteDashboardShell active="shipping" dashboard={dashboard}>
      <h2 className="text-lg font-bold font-display text-[#0F172A] mb-4">Shipping</h2>
      {!shipments ? <p className="text-sm text-[#475569]">Loading…</p> : shipments.length === 0 ? (
        <p className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-sm text-[#94A3B8]">Nothing ready to ship. Shipments you create from an order will appear here.</p>
      ) : (
        <div className="space-y-6">
          {GROUPS.map(([status, label]) => {
            const items = shipments.filter((s) => s.status === status);
            if (items.length === 0) return null;
            return (
              <div key={status}>
                <p className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] mb-2">{label} ({items.length})</p>
                <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
                  {items.map((s) => (
                    <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E2E8F0] last:border-0 px-4 py-3.5">
                      <div>
                        <Link to={`/account/websites/${slug}/orders/${s.order_id}`} className="text-sm font-medium text-[#10B981] hover:underline">View order</Link>
                        <p className="text-xs text-[#94A3B8]">{s.carrier || "Carrier not specified"} — {s.tracking_number || "no tracking number"}
                          {s.tracking_url && <> · <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="underline">Track Shipment</a></>}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {(ACTIONS_BY_STATUS[status] || []).map(([action, actionLabel]) => (
                          <button key={action} onClick={() => changeSiteShipmentStatus(token, slug, s.id, action).then(load)}
                            className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium hover:border-[#10B981]">{actionLabel}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SiteDashboardShell>
  );
}
